import React, { useState, useEffect, useRef } from "react";
import CampaignItem from "../campaign/CampaignItem";
import SessionItem from "../campaign/SessionItem";
import CampaignsListWrapper from "../../layouts/CampaignsListWrapper";
import { IoAddCircleOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import { FiEdit2 } from "react-icons/fi";
import EditCampaignModal from "../modals/EditCampaignModal";
import SessionEditor from "../modals/SessionEditor";
import API_BASE_URL from "../../../../config";

const CampaignSidebar = ({
  origin = "",
  campaigns = [],
  selectedCampaign = "",
  setSelectedCampaign = () => {},
  campaignDetails = {},
  setSelectedSession = () => {},
  selectedSession = null,
  onClose = () => {},
  isOpen = false,
  setIsModalOpen = () => {},
  handleSessionSelect = () => {},
  onSessionOperation = async () => {},
  initialSelectionMade = { current: false },
}) => {
  const selectedSessionRef = useRef(null);
  const [editingSession, setEditingSession] = useState(null);

  // Ensure campaigns is an array and not null
  const validCampaigns = Array.isArray(campaigns) ? campaigns : [];

  // Sort campaigns: first by presence of transcripts, then by most recent edit
  const sortedCampaigns = [...validCampaigns].sort((a, b) => {
    // Skip sorting if either campaign is invalid
    if (!a?.id || !b?.id) return 0;
    
    // Get campaign details, defaulting to empty objects with empty transcript arrays
    const aDetails = campaignDetails[a.id] || { transcripts: [] };
    const bDetails = campaignDetails[b.id] || { transcripts: [] };
    
    // Ensure transcripts arrays exist and are arrays
    const aTranscripts = Array.isArray(aDetails.transcripts) ? aDetails.transcripts : [];
    const bTranscripts = Array.isArray(bDetails.transcripts) ? bDetails.transcripts : [];
    
    // Compare by presence of transcripts - campaigns with transcripts come first
    const aHasTranscripts = aTranscripts.length > 0;
    const bHasTranscripts = bTranscripts.length > 0;

    // This is the primary sort criteria - campaigns with transcripts come first
    if (aHasTranscripts && !bHasTranscripts) return -1;
    if (!aHasTranscripts && bHasTranscripts) return 1;

    // If both have transcripts or both don't have transcripts, sort by most recent edit
    // Most recently edited comes first (hence the reversed order in the subtraction)
    const aLastEdited = aDetails.lastEdited || (aTranscripts[0]?.created_at) || new Date(0).toISOString();
    const bLastEdited = bDetails.lastEdited || (bTranscripts[0]?.created_at) || new Date(0).toISOString();

    return new Date(bLastEdited) - new Date(aLastEdited);
  });

  // Log the sorted campaigns
  useEffect(() => {
    console.log('Original campaigns:', validCampaigns);
    console.log('Sorted campaigns:', sortedCampaigns);
    
    if (sortedCampaigns.length > 0) {
      console.log('First campaign in sorted list:', sortedCampaigns[0]);
    }
  }, [validCampaigns, sortedCampaigns]);

  // Select the first campaign if none is selected
  useEffect(() => {
    // Only attempt to select a campaign if we have campaign details
    const hasDetails = Object.keys(campaignDetails).length > 0;
    
    console.log('CampaignSidebar selection check:', {
      sortedCampaignsLength: sortedCampaigns.length,
      selectedCampaign,
      initialSelectionMade: initialSelectionMade.current,
      hasDetails
    });
    
    // Check if we should auto-select the first campaign
    // Only do this if:
    // 1. We have campaigns to select from
    // 2. No campaign is currently selected
    // 3. No initial selection has been made (from URL parameters or elsewhere)
    // 4. We have campaign details loaded
    if (sortedCampaigns.length > 0 && !selectedCampaign && !initialSelectionMade.current && hasDetails) {
      const firstCampaign = sortedCampaigns[0];
      if (firstCampaign && firstCampaign.id) {
        console.log('Auto-selecting first campaign from sorted list:', firstCampaign.id, firstCampaign.title);
        setSelectedCampaign(firstCampaign.id);
        initialSelectionMade.current = true;
      }
    }
  }, [sortedCampaigns, selectedCampaign, setSelectedCampaign, campaignDetails]);

  // Automatically select the first transcript on page load
  useEffect(() => {
    // Remove automatic selection of the first session
    // Only select a session if explicitly directed by the Archive component
    // The following code is commented out to prevent auto-selection
    /*
    if (!selectedSession) { // Only run if no session is selected
      for (const campaign of sortedCampaigns) {
        if (!campaign?.id) continue;
        const details = campaignDetails[campaign.id] || { transcripts: [] };
        const transcripts = Array.isArray(details.transcripts) ? details.transcripts : [];
        if (transcripts.length > 0) {
          setSelectedSession(transcripts[0].id);
          break;
        }
      }
    }
    */
  }, [sortedCampaigns, campaignDetails, setSelectedSession, selectedSession]);

  // Scroll the selected session into view
  useEffect(() => {
    if (selectedSessionRef.current) {
      selectedSessionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedSession]);

  // Add debug logging
  useEffect(() => {
    console.log('CampaignSidebar - selectedCampaign:', selectedCampaign);
    console.log('CampaignSidebar - selectedSession:', selectedSession);
    console.log('CampaignSidebar - campaignDetails:', campaignDetails);
    
    if (selectedCampaign && campaignDetails[selectedCampaign]) {
      console.log('CampaignSidebar - Selected campaign transcripts:', 
        campaignDetails[selectedCampaign].transcripts);
    }
  }, [selectedCampaign, selectedSession, campaignDetails]);

  // Add debug logging for props
  useEffect(() => {
    console.log('CampaignSidebar props:', {
      origin,
      selectedCampaign,
      selectedSession,
      initialSelectionMade: initialSelectionMade.current,
      campaignsCount: campaigns.length,
      campaignDetailsCount: Object.keys(campaignDetails).length
    });
  }, [origin, selectedCampaign, selectedSession, initialSelectionMade.current, campaigns.length, campaignDetails]);

  const handleEditSession = (e, session) => {
    e.stopPropagation();
    setEditingSession(session);
  };

  const handleRenameSession = async (sessionId, newName) => {
    await onSessionOperation(async () => {
      const response = await fetch(`${API_BASE_URL}/api/update_title/${sessionId}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ new_title: newName }),
      });

      if (!response.ok) {
        throw new Error("Failed to rename session");
      }
    });
  };

  const handleMoveSession = async (sessionId, newCampaignId) => {
    await onSessionOperation(async () => {
      const response = await fetch(`${API_BASE_URL}/api/move_transcript/${sessionId}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ new_campaign_id: newCampaignId }),
      });

      if (!response.ok) {
        throw new Error("Failed to move session");
      }
    });
  };

  const handleDeleteSession = async (sessionId, password) => {
    return await onSessionOperation(async () => {
      const response = await fetch(`${API_BASE_URL}/api/delete_transcript/${sessionId}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ password }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to delete session");
      }
      
      return { success: true };
    });
  };

  const handleCampaignSelect = (campaignId) => {
    console.log('CampaignSidebar - handleCampaignSelect called with:', campaignId, 'Current selected:', selectedCampaign);
    
    // Use the setSelectedCampaign prop directly, which now handles URL updates in the parent component
    setSelectedCampaign(campaignId);
  };

  return (
    <>
      <CampaignsListWrapper
        onClose={onClose}
        isOpen={isOpen}
        campaigns={sortedCampaigns}>
        <div className="space-y-2">
          <div className="space-y-2">
            {sortedCampaigns.map((campaign) => {
              // Skip invalid campaigns
              if (!campaign || !campaign.id) return null;
              
              // Get campaign details with fallback to empty object with empty transcripts array
              const campaignDetail = campaignDetails[campaign.id] || { transcripts: [] };
              
              // Ensure transcripts is an array
              const transcripts = Array.isArray(campaignDetail.transcripts) 
                ? campaignDetail.transcripts 
                : [];
              
              return (
                <CampaignItem
                  key={campaign.id}
                  campaign={campaign}
                  isSelected={selectedCampaign === campaign.id && !selectedSession}
                  onSelect={handleCampaignSelect}
                >
                  <div className="flex justify-between items-center">
                    <div className="pl-4 space-y-1 mt-2 text-primary font-semibold">
                      {/* Map over transcripts with additional safety checks */}
                      {transcripts
                        .filter(transcript => transcript && transcript.id)
                        .map((transcript) => {
                          const isSelected = String(selectedSession) === String(transcript.id);
                          return (
                            <SessionItem
                              key={transcript.id}
                              session={transcript}
                              isSelected={isSelected}
                              onSelect={setSelectedSession}
                              onEdit={handleEditSession}
                              ref={selectedSession === transcript.id ? selectedSessionRef : null}
                            />
                          );
                        })}
                      {origin === "archive" && (
                        <Link
                          to={`/dashboard?campaign=${campaign.id}`}
                          className="w-full flex items-center font-normal justify-center bg-sky-800/10 hover:bg-primary-light space-x-2 text-sky-800 hover:text-white rounded-lg p-2">
                          <IoAddCircleOutline size={20} />
                          <span>Add Session</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </CampaignItem>
              );
            })}
          </div>
        </div>
        {origin === "archive" && (
          <>
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full flex items-center justify-center bg-sky-800/10 hover:bg-primary-light space-x-2 text-sky-800 hover:text-white rounded-lg p-2 mt-2">
              <IoAddCircleOutline size={20} />
              <span>Add New Campaign</span>
            </button>
          </>
        )}
      </CampaignsListWrapper>

      <SessionEditor
        isOpen={!!editingSession}
        onClose={() => setEditingSession(null)}
        session={editingSession}
        campaigns={validCampaigns}
        onRename={handleRenameSession}
        onMove={handleMoveSession}
        onDelete={handleDeleteSession}
      />
    </>
  );
};

export default CampaignSidebar;
